// Progressive enhancement for the email-signup block's "Klaviyo" mode.
// Intercepts the form submit and subscribes the email to a Klaviyo list via the
// public Client Subscriptions API (company_id only — no private key). Falls back
// to the normal Shopify form post if JS fails to load.
//
// Docs: https://developers.klaviyo.com/en/reference/create_client_subscription

const KLAVIYO_REVISION = '2024-10-15';

class KlaviyoSignupForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.messageEl = this.querySelector('[data-klaviyo-message]');
    this.companyId = this.dataset.companyId;
    this.listId = this.dataset.listId;
    if (!this.form || !this.companyId) return;
    this.form.addEventListener('submit', (e) => this.onSubmit(e));
  }

  async onSubmit(event) {
    event.preventDefault();
    const emailInput = this.form.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();
    if (!email) return;

    this.setBusy(true);
    this.hideMessage();

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.companyId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(this.payload(email)),
        }
      );

      // The endpoint returns 202 Accepted on success.
      if (res.ok || res.status === 202) {
        this.showMessage('success');
        const track = /** @type {any} */ (window).track;
        if (typeof track === 'function') {
          track('newsletter_signup', { location: 'footer', list_source: 'klaviyo' });
        }
        this.form.reset();
      } else {
        this.showMessage('error');
      }
    } catch (e) {
      this.showMessage('error');
    } finally {
      this.setBusy(false);
    }
  }

  payload(email) {
    const attributes = {
      profile: { data: { type: 'profile', attributes: { email } } },
    };
    const data = { type: 'subscription', attributes };
    // Only attach a list relationship when a list id is configured; without it
    // the profile is created/updated but not subscribed to a specific list.
    if (this.listId) {
      data.relationships = { list: { data: { type: 'list', id: this.listId } } };
    }
    return { data };
  }

  setBusy(busy) {
    const button = this.form.querySelector('button');
    if (button) {
      button.toggleAttribute('disabled', busy);
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
  }

  showMessage(type) {
    if (!this.messageEl) return;
    const text =
      type === 'success'
        ? this.dataset.successText || 'Thanks for subscribing.'
        : this.dataset.errorText || 'Something went wrong. Please try again.';
    this.messageEl.textContent = text;
    this.messageEl.dataset.state = type;
    this.messageEl.hidden = false;
  }

  hideMessage() {
    if (this.messageEl) this.messageEl.hidden = true;
  }
}

if (!customElements.get('klaviyo-signup-form')) {
  customElements.define('klaviyo-signup-form', KlaviyoSignupForm);
}
